import { DownOutlined } from "@ant-design/icons";
import { Select } from "antd";

// A type alias (not an interface) so it satisfies antd's option index signature.
export type WidgetSelectOption<T extends string | number> = {
	value: T;
	label: string;
	/** Muted suffix, e.g. "every tenant". */
	note?: string;
};

interface WidgetSelectProps<T extends string | number> {
	value: T;
	options: WidgetSelectOption<T>[];
	onChange: (value: T) => void;
	ariaLabel: string;
	/** Show the selected option's note in the closed select too. */
	noteInLabel?: boolean;
}

const renderLabel = (label: string, note?: string) =>
	note ? (
		<>
			{label}
			<span className="dashboard-select__note">{note}</span>
		</>
	) : (
		label
	);

/**
 * Compact tenant or period picker in a widget header. A single option shows
 * as plain text, since there is nothing to pick.
 */
function WidgetSelect<T extends string | number>({
	value,
	options,
	onChange,
	ariaLabel,
	noteInLabel = false,
}: WidgetSelectProps<T>) {
	if (options.length === 1) {
		const [only] = options;
		return (
			<span className="dashboard-select dashboard-select--static">
				{renderLabel(only.label, noteInLabel ? only.note : undefined)}
			</span>
		);
	}

	return (
		<Select<T, WidgetSelectOption<T>>
			className="dashboard-select"
			variant="borderless"
			size="small"
			value={value}
			options={options}
			onChange={(selected) => onChange(selected)}
			aria-label={ariaLabel}
			popupMatchSelectWidth={false}
			suffixIcon={<DownOutlined />}
			optionRender={({ data }) => renderLabel(data.label, data.note)}
			labelRender={({ label, value: selected }) => {
				const option = options.find((item) => item.value === selected);
				return option
					? renderLabel(option.label, noteInLabel ? option.note : undefined)
					: label;
			}}
		/>
	);
}

export default WidgetSelect;
